// src/pages/analytics/BestellungErfassenDialog.tsx
// Modul 38 A2: Aus der Reichweite heraus ECHTE Bestellungen im Einkaufsmodul anlegen.
//
// Loest den Weg ueber Vormerkungen ab (PurchaseNoteDialog). Der wesentliche Unterschied
// ist nicht die Optik, sondern die Aufteilung: angehakte Artikel gehoeren oft zu
// VERSCHIEDENEN Lieferanten, und eine Bestellung geht immer an genau einen. Der Dialog
// zeigt die Aufteilung deshalb vorher an, statt sie stillschweigend zu treffen.
//
// Abgeschickt wird EIN Aufruf fuer alle Gruppen (POST /purchase/orders/batch/): der
// Server legt alle an oder keine. Mehrere Aufrufe koennten teilweise durchlaufen, und
// ein Wiederholungsversuch wuerde doppelt anlegen.
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { GrundartikelLink } from "@/components/catalog/GrundartikelLink";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ApiError, isTransportError } from "@/api/client";
import { useSuppliers } from "@/api/dashboard";
import { useCreatePurchaseOrdersBatch, type BatchCreatedOrder, type BatchCreateOrdersInput } from "@/api/purchase";
import { gruppiereNachLieferant, type BestellArtikel } from "@/lib/inventory-reach";
import { leseOffeneBestellung, speichereOffeneBestellung, entferneOffeneBestellung } from "@/lib/reach-pending-order";

export type BestellZeile = BestellArtikel;

export interface BestellungErfassenDialogProps {
  includeDead?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Die angehakten Zeilen der Reichweite. */
  zeilen: BestellZeile[];
  /** Mengen aus der Spalte "Bestellen", key = ga_id. Zeilen ohne Menge > 0 fallen raus. */
  mengen: Record<number, string>;
  /** Nach erfolgreicher Anlage — die Seite hebt die Auswahl auf und leert die Mengen. */
  onAngelegt: () => void;
  onPendingChange?: (pending: boolean) => void;
}

/** Heute als YYYY-MM-DD im lokalen Kalender (das Bestelldatum ist ein Kalendertag,
 *  keine Zeitangabe — toISOString() waere UTC und am spaeten Abend der Vortag). */
function heute(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const t = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${t}`;
}

export function istGueltigesBestelldatum(datum: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datum)) return false;
  const [jahr, monat, tag] = datum.split("-").map(Number) as [number, number, number];
  if (jahr < 1 || monat < 1 || monat > 12 || tag < 1) return false;
  const schaltjahr = jahr % 4 === 0 && (jahr % 100 !== 0 || jahr % 400 === 0);
  return tag <= [31, schaltjahr ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][monat - 1]!;
}

interface GruppenEingabe {
  supplierId: number | null;
  orderNumber: string;
  eigeneNummer: boolean;
}

export default function BestellungErfassenDialog({
  open, onOpenChange, zeilen, mengen, onAngelegt, onPendingChange, includeDead = false,
}: BestellungErfassenDialogProps) {
  const suppliers = useSuppliers();
  const batch = useCreatePurchaseOrdersBatch();
  const [gespeicherterVorgang] = useState(leseOffeneBestellung);
  const gespeichert = gespeicherterVorgang?.request ?? null;

  const aktuelleGruppen = useMemo(() => gruppiereNachLieferant(zeilen, mengen), [zeilen, mengen]);
  const [eingefroreneGruppen, setEingefroreneGruppen] = useState<typeof aktuelleGruppen | null>(() => gespeichert ? gespeichert.groups.map((g, i) => ({
    supplierId: -(i + 1), supplierName: `Lieferant #${g.supplier_id}`,
    zeilen: g.items.map(z => ({ ga_id: z.stock_item_id, sku: `Artikel #${z.stock_item_id}`, name: "", supplier_id: g.supplier_id, supplier_name: null, menge: z.qty })),
    mengeGesamt: g.items.reduce((n, z) => n + z.qty, 0),
  })) : null);
  const gruppen = eingefroreneGruppen ?? aktuelleGruppen;

  const [bestelldatum, setBestelldatum] = useState(gespeichert?.groups[0]?.order_date ?? heute());
  const [datumFehler, setDatumFehler] = useState(false);
  const [alsEntwurf, setAlsEntwurf] = useState(gespeichert?.groups[0]?.status === "entwurf");
  const [eingaben, setEingaben] = useState<Record<string, GruppenEingabe>>(() => Object.fromEntries((gespeichert?.groups ?? []).map((g, i) => [String(-(i + 1)), {supplierId: g.supplier_id, orderNumber: g.order_number ?? "", eigeneNummer: !!g.order_number}])));
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweise, setHinweise] = useState<string[]>([]);
  const [ergebnis, setErgebnis] = useState<BatchCreatedOrder[] | null>(null);
  // EINMAL je Vorgang erzeugt und bei einem Wiederholungsversuch unveraendert
  // mitgeschickt — sonst legt ein zweiter Klick eine zweite Bestellung an.
  const [batchId, setBatchId] = useState<string>("");
  const [offenerRequest, setOffenerRequest] = useState<BatchCreateOrdersInput | null>(gespeichert);
  const [letzterVersuch, setLetzterVersuch] = useState(() => gespeicherterVorgang ? { id: gespeicherterVorgang.lastAttemptId, at: gespeicherterVorgang.lastAttemptAt } : null);
  const [jetzt, setJetzt] = useState(Date.now);
  const sendet = useRef(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const ungueltige = zeilen.filter(z => {
    const raw = (mengen[z.ga_id] ?? "").trim();
    return raw !== "" && (!/^\d+$/.test(raw) || Number(raw) < 1 || Number(raw) > 1000000);
  });

  useEffect(() => {
    if (!offenerRequest || !letzterVersuch) return;
    const rest = letzterVersuch.at + 120000 - Date.now();
    setJetzt(Date.now());
    if (rest <= 0) return;
    const timer = setTimeout(() => setJetzt(Date.now()), rest);
    return () => clearTimeout(timer);
  }, [offenerRequest, letzterVersuch]);

  useEffect(() => {
    if (!open) return;
    if (offenerRequest) return;
    setBestelldatum(heute());
    setDatumFehler(false);
    setAlsEntwurf(false);
    setFehler(null);
    setHinweise([]);
    setErgebnis(null);
    setBatchId(crypto.randomUUID());
    const start: Record<string, GruppenEingabe> = {};
    for (const g of gruppen) {
      start[schluessel(g.supplierId)] = {
        supplierId: g.supplierId, orderNumber: "", eigeneNummer: false,
      };
    }
    setEingaben(start);
    // gruppen bewusst NICHT in der Abhaengigkeitsliste: waehrend der Dialog offen ist,
    // soll das Tippen im Mengenfeld dahinter die Eingaben hier nicht zuruecksetzen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const offeneLieferantenwahl = gruppen.some(
    (g) => g.supplierId === null && (eingaben[schluessel(null)]?.supplierId ?? null) === null,
  );
  const artikelGesamt = gruppen.reduce((n, g) => n + g.zeilen.length, 0);

  function setzeEingabe(key: string, patch: Partial<GruppenEingabe>): void {
    setEingaben((alt) => ({ ...alt, [key]: { ...alt[key]!, ...patch } }));
  }

  async function absenden(): Promise<void> {
    if (sendet.current) return;
    setFehler(null);
    if (!offenerRequest && !istGueltigesBestelldatum(bestelldatum)) {
      setDatumFehler(true);
      return;
    }
    setDatumFehler(false);
    if (!offenerRequest && gruppen.some(g => !eingaben[schluessel(g.supplierId)])) {
      setFehler("Eingaben für eine Lieferantengruppe fehlen. Bitte Dialog erneut öffnen.");
      return;
    }
    const groups = offenerRequest ? [] : gruppen.map((g) => {
      const e = eingaben[schluessel(g.supplierId)] as GruppenEingabe;
      const nummer = e.eigeneNummer ? e.orderNumber.trim() : "";
      return {
        supplier_id: (e.supplierId ?? g.supplierId)!,
        order_date: bestelldatum,
        status: alsEntwurf ? ("entwurf" as const) : ("bestellt" as const),
        ...(nummer !== "" ? { order_number: nummer } : {}),
        items: g.zeilen.map((z) => ({ stock_item_id: z.ga_id, qty: z.menge })),
      };
    });

    const request: BatchCreateOrdersInput = offenerRequest ?? {
      client_batch_id: batchId, source: "reichweite", include_dead: includeDead, groups,
    };
    const versuch = { id: crypto.randomUUID(), at: Date.now() };
    sendet.current = true;
    try {
      if (!speichereOffeneBestellung({request, lastAttemptId: versuch.id, lastAttemptAt: versuch.at})) {
        setFehler("Browser-Speicher nicht verfügbar – Bestellung kann nicht sicher gesendet werden");
        return;
      }
      setLetzterVersuch(versuch);
      setOffenerRequest(request);
      setEingefroreneGruppen(gruppen);
      onPendingChange?.(true);
      const antwort = await batch.mutateAsync(request);
      if (!entferneOffeneBestellung(request.client_batch_id, versuch.id)) return;
      setOffenerRequest(null);
      setEingefroreneGruppen(null);
      onPendingChange?.(false);
      // Defensiv gegen eine Antwort ohne orders: der Erfolgsfall darf NICHT abstuerzen.
      // Ein Absturz genau hier waere besonders teuer — die Bestellung ist zu diesem
      // Zeitpunkt bereits angelegt, der Nutzer saehe aber einen Fehler und wuerde erneut
      // absenden. (Die Idempotenz faengt das ab, aber verlassen sollte man sich darauf
      // nicht: die Anzeige soll stimmen, nicht nur die Datenlage.)
      const angelegte = Array.isArray(antwort.orders) ? antwort.orders : [];
      setErgebnis(angelegte);
      setHinweise((antwort.uebersprungen ?? []).map((z) => `${z.sku}: ${z.hinweis}`));
      toast.success(
        antwort.wiederholung
          ? "Dieser Vorgang war bereits gespeichert — es wurde nichts doppelt angelegt."
          : angelegte.length === 0 ? 'Keine Bestellung angelegt — alle Positionen wurden übersprungen.'
          : `${angelegte.length === 1 ? "Bestellung" : `${angelegte.length} Bestellungen`} angelegt.`,
      );
      onAngelegt();
    } catch (err) {
      if (err instanceof ApiError && !isTransportError(err) && [400, 409, 422].includes(err.status)) {
        if (!entferneOffeneBestellung(request.client_batch_id, versuch.id)) return;
        setOffenerRequest(null);
        setEingefroreneGruppen(null);
        onPendingChange?.(false);
        setBatchId(crypto.randomUUID());
      }
      // Die Antwort des Servers im Klartext zeigen: sie nennt die Gruppe und den Grund
      // (belegte Nummer, falsches Format, fremde Vormerkung). Eine eigene Formulierung
      // waere hier schlechter — sie wuerde die genaue Ursache verschlucken.
      setFehler(err instanceof ApiError ? err.message : "Ausgang unbekannt. Bitte denselben Vorgang erneut senden.");
    } finally {
      sendet.current = false;
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        {hinweise.length > 0 && <div role="status" className="rounded-md border border-line bg-warn-soft p-3 text-sm text-warn">
          {hinweise.map((text) => <p key={text}>{text}</p>)}
        </div>}
        <DialogHeader>
          <DialogTitle>Bestellung erfassen</DialogTitle>
          <DialogDescription>
            {ergebnis !== null ? "Bestellvorgang abgeschlossen." : gruppen.length === 0
              ? "Keine Zeile hat eine Menge. Trage in der Spalte Bestellen ein, wie viel bestellt werden soll."
              : `${artikelGesamt} Artikel in ${gruppen.length} ${
                  gruppen.length === 1 ? "Bestellung" : "Bestellungen"
                } — eine je Lieferant.`}
          </DialogDescription>
        </DialogHeader>

        {ergebnis !== null ? (
          <div className="space-y-2">
            {ergebnis.map((o) => (
              <div key={o.id} className="flex items-center justify-between rounded border border-line px-3 py-2 text-sm">
                <span>
                  <span className="font-mono">{o.order_number}</span>
                  <span className="text-muted"> · {o.supplier_name ?? "ohne Lieferant"} · {o.item_count} Pos. · {o.qty_total.toLocaleString("de-DE")} Stück</span>
                </span>
                <Link to={`/einkauf/${o.id}`} className="text-xs underline underline-offset-2">
                  Zur Bestellung
                </Link>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {!offenerRequest && ungueltige.map(z => <p role="alert" key={z.ga_id} className="text-sm text-destructive">{z.sku}: Menge muss ganzzahlig zwischen 1 und 1.000.000 sein.</p>)}
            {offenerRequest && <p className="text-sm text-ink-2">Der gesendete Vorgang ist gesperrt, bis sein Ergebnis geklärt ist. Vorher unter Einkauf prüfen, ob die Bestellung schon angelegt ist. {letzterVersuch && jetzt < letzterVersuch.at + 120000 && `Verwerfen ab ${new Date(letzterVersuch.at + 120000).toLocaleTimeString("de-DE", {hour: "2-digit", minute: "2-digit"})} möglich.`}</p>}
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1">
                <Label htmlFor="bestelldatum">Bestelldatum</Label>
                <Input
                  id="bestelldatum" type="date" className="w-40" disabled={offenerRequest !== null || batch.isPending}
                  value={bestelldatum} onChange={(e) => { setBestelldatum(e.target.value); setDatumFehler(false); }}
                  aria-invalid={datumFehler} aria-describedby={datumFehler ? "bestelldatum-fehler" : undefined}
                />
                {datumFehler && <p id="bestelldatum-fehler" role="alert" className="text-xs text-destructive">Bitte ein gültiges Bestelldatum eingeben.</p>}
                <p className="text-xs text-muted">Bestimmt den Monat in der Bestellnummer.</p>
              </div>
              <label className="flex items-center gap-2 pb-6 text-sm">
                <Checkbox disabled={offenerRequest !== null || batch.isPending} checked={alsEntwurf} onCheckedChange={(v) => setAlsEntwurf(v === true)} />
                Nur als Entwurf anlegen
              </label>
            </div>

            {gruppen.map((g) => {
              const key = schluessel(g.supplierId);
              const e = eingaben[key];
              if (!e) return null;
              const ohneLieferant = g.supplierId === null;
              return (
                <div
                  key={key}
                  className={`space-y-3 rounded border px-3 py-3 ${
                    ohneLieferant && e.supplierId === null ? "border-amber-500/60" : "border-line"
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm font-medium">
                      {g.supplierName ?? "Ohne Lieferant"}
                    </span>
                    <span className="text-xs text-muted">
                      {g.zeilen.length} Pos. · {g.mengeGesamt.toLocaleString("de-DE")} Stück
                    </span>
                  </div>

                  {ohneLieferant && (
                    <div className="space-y-1">
                      <Label>Lieferant wählen</Label>
                      <Select
                        disabled={offenerRequest !== null || batch.isPending}
                        value={e.supplierId === null ? "" : String(e.supplierId)}
                        onValueChange={(v) => setzeEingabe(key, { supplierId: Number(v) })}
                      >
                        <SelectTrigger className="w-64"><SelectValue placeholder="Lieferant auswählen" /></SelectTrigger>
                        <SelectContent>
                          {(suppliers.data?.suppliers ?? [])
                            .filter((s) => s.is_active)
                            .map((s) => (
                              <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted">
                        Diese Artikel haben in DreamRobot keinen Lieferanten. Die Auswahl gilt nur
                        für diese Bestellung und wird nicht nach DreamRobot zurückgeschrieben.
                      </p>
                    </div>
                  )}

                  <div className="space-y-1">
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox
                        disabled={offenerRequest !== null || batch.isPending}
                        checked={e.eigeneNummer}
                        onCheckedChange={(v) => setzeEingabe(key, { eigeneNummer: v === true })}
                      />
                      Eigene Bestellnummer (Nacherfassung)
                    </label>
                    {e.eigeneNummer ? (
                      <Input
                        disabled={offenerRequest !== null || batch.isPending}
                        className="w-56 font-mono"
                        placeholder="z. B. 161082026161"
                        value={e.orderNumber}
                        onChange={(ev) => setzeEingabe(key, { orderNumber: ev.target.value })}
                      />
                    ) : (
                      <p className="text-xs text-muted">Nummer wird automatisch vergeben.</p>
                    )}
                  </div>

                  <ul className="space-y-0.5 text-xs text-muted">
                    {g.zeilen.slice(0, expanded[key] ? undefined : 6).map((z) => (
                      <li key={z.ga_id} className="flex justify-between gap-3">
                        <span className="min-w-0"><span><GrundartikelLink code={z.merchant_sku || z.sku} /> · {z.name}</span>
                          {!!z.bausatz_teile?.length && <span className="block whitespace-normal">
                            für Lieferant: {z.bausatz_teile.map((t, i) => <span key={t.ga_id}>{i > 0 && " + "}{(z.menge * t.menge).toLocaleString("de-DE")}× <GrundartikelLink code={t.sku} /></span>)}
                          </span>}
                        </span>
                        <span className="tabular-nums">{z.menge.toLocaleString("de-DE")}</span>
                      </li>
                    ))}
                    {g.zeilen.length > 6 && <li><button type="button" className="underline" onClick={() => setExpanded(v => ({...v, [key]: !v[key]}))}>{expanded[key] ? "Weniger anzeigen" : `+ ${g.zeilen.length - 6} weitere`}</button></li>}
                  </ul>
                </div>
              );
            })}

            {fehler && (
              <p role="alert" className="text-sm text-red-600">{fehler}</p>
            )}
          </div>
        )}

        <DialogFooter>
          {ergebnis !== null ? (
            <Button onClick={() => onOpenChange(false)}>Schließen</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Abbrechen</Button>
              {offenerRequest && <Button variant="outline" disabled={batch.isPending || !letzterVersuch || jetzt < letzterVersuch.at + 120000} onClick={() => {
                if (!letzterVersuch || Date.now() < letzterVersuch.at + 120000 || !entferneOffeneBestellung(offenerRequest.client_batch_id, letzterVersuch.id)) return;
                setOffenerRequest(null); setEingefroreneGruppen(null); setFehler(null);
                setBatchId(crypto.randomUUID()); onPendingChange?.(false);
              }}>Vorgang verwerfen</Button>}
              <Button
                onClick={() => void absenden()}
                disabled={batch.isPending || (!offenerRequest && (gruppen.length === 0 || offeneLieferantenwahl || ungueltige.length > 0))}
              >
                {batch.isPending
                  ? "Wird angelegt …"
                  : offenerRequest ? "Erneut senden" : gruppen.length === 1 ? "Bestellung anlegen" : `${gruppen.length} Bestellungen anlegen`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function schluessel(supplierId: number | null): string {
  return supplierId === null ? "ohne" : String(supplierId);
}
